import { useState, useEffect } from 'react';
import './styles/global.css';
import { useAuth } from './hooks/useAuth';
import { useProfile } from './hooks/useProfile';
import AuthPage from './components/auth/AuthPage';
import AppShell from './components/layout/AppShell';
import HomePage from './pages/HomePage';
import ProfilePage from './pages/ProfilePage';
import CommunityPage from './pages/CommunityPage';
import DetoxPage from './pages/DetoxPage';
import ParaVocePage from './pages/ParaVocePage';
import YouTubePage from './pages/YouTubePage';
import { useYouTubeFeed } from './hooks/useYouTubeFeed';
import { useHomeContent } from './hooks/useHomeContent';
import { useParaVoceBadge } from './hooks/useParaVoceBadge';
import OnboardingPage from './pages/OnboardingPage';
import QuizPage from './pages/QuizPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PrivacidadePage from './pages/PrivacidadePage';
import LGPDConsent from './components/LGPDConsent';
import JornadaPage from './pages/JornadaPage';

// Rotas públicas
const pathname = window.location.pathname;
const isQuizRoute = pathname === '/quiz';
const isResetRoute = pathname === '/reset-password';

export default function App() {
  const { user, loading: authLoading, recoveryMode, signIn, signUp, signOut, resetPassword, updatePassword } = useAuth();
  const { profile, needsSetup, profileLoaded, saveProfile, fetchProfile } = useProfile(user?.id);
  const [page, setPage] = useState('home');
  const { videos, shorts, playlists, showBadge, markAsSeen, channelInfo } = useYouTubeFeed();
  const { homeSlides, homeShorts, homeAgenda } = useHomeContent();
  const { showParaVoceBadge, markParaVoceSeen } = useParaVoceBadge();
  const [onboardingFeito, setOnboardingFeito] = useState(false);
  const [showPrivacidade, setShowPrivacidade] = useState(false);
  const [lgpdAceito, setLgpdAceito] = useState(false);

  // Reseta per-user ao trocar de conta na mesma sessão
  useEffect(() => {
    setOnboardingFeito(false);
    setLgpdAceito(false);
  }, [user?.id]); // eslint-disable-line

  // LGPD: fonte de verdade é o perfil no Supabase
  useEffect(() => {
    if (profileLoaded) {
      setLgpdAceito(!!profile?.lgpd_consent_at);
    }
  }, [profile, profileLoaded]);

  // Quiz é público — qualquer pessoa acessa sem estar logada
  if (isQuizRoute) {
    return <QuizPage />;
  }

  // Redefinição de senha — detectada pelo evento PASSWORD_RECOVERY do Supabase
  if (recoveryMode || isResetRoute) {
    return <ResetPasswordPage onUpdatePassword={updatePassword} />;
  }

  if (authLoading || (user && !profileLoaded)) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!user) {
    return <AuthPage onLogin={signIn} onSignUp={signUp} onResetPassword={resetPassword} />;
  }

  if (needsSetup && !onboardingFeito) {
    return (
      <OnboardingPage
        user={user}
        profile={profile}
        onSave={saveProfile}
        onComplete={() => setOnboardingFeito(true)}
      />
    );
  }

  // Página de política de privacidade (sem shell, full-screen)
  if (showPrivacidade) {
    return <PrivacidadePage onVoltar={() => setShowPrivacidade(false)} />;
  }

  return (
    <>
      {/* Banner LGPD — aparece sobre tudo enquanto não houver consentimento */}
      {!lgpdAceito && (
        <LGPDConsent
          userId={user.id}
          onAceitar={() => setLgpdAceito(true)}
          onVerPolitica={() => setShowPrivacidade(true)}
        />
      )}

      <AppShell
        user={user}
        profile={profile}
        onLogout={signOut}
        page={page}
        onNavigate={setPage}
        showBadge={showBadge}
        showHomeBadge={showParaVoceBadge}
      >
        {page === 'home' && (
          <HomePage
            user={user}
            profile={profile}
            needsSetup={needsSetup}
            onNavigateToProfile={() => setPage('profile')}
            onProfileRefresh={fetchProfile}
            onNavigate={setPage}
            homeSlides={homeSlides}
            homeShorts={homeShorts}
            homeAgenda={homeAgenda}
          />
        )}
        {page === 'community' && (
          <CommunityPage user={user} />
        )}
        {page === 'detox' && (
          <DetoxPage user={user} />
        )}
        {page === 'paravoce' && (
          <ParaVocePage user={user} markParaVoceSeen={markParaVoceSeen} />
        )}
        {page === 'youtube' && (
          <YouTubePage
            videos={videos}
            shorts={shorts}
            playlists={playlists}
            markAsSeen={markAsSeen}
            channelInfo={channelInfo}
          />
        )}
        {page === 'jornada' && (
          <JornadaPage onBack={() => setPage('home')} />
        )}
        {page === 'profile' && (
          <ProfilePage
            user={user}
            profile={profile}
            needsSetup={needsSetup}
            onSave={saveProfile}
            onLogout={signOut}
            onVerPolitica={() => setShowPrivacidade(true)}
            onExcluirConta={signOut}
          />
        )}
      </AppShell>
    </>
  );
}
